import { MessageSquareCheck, MessageSquareX } from 'lucide-react';

import type { FlowCardRef } from '@/lib/flow-utils';

/**
 * How Flow names a feedback CARD.
 *
 * Deliberately not the answer chip. That one — a tone-filled pill with a bare check, cross or dash —
 * means "this answer", and using it here made a rung look like it was matching answers rather than
 * naming the cards a block can fire. Two different things cannot wear the same clothes.
 *
 * A card is a message, so it is a message bubble, with the card's own evaluation mark INSIDE it. One
 * glyph carries both facts, which also keeps the mark doing the work and colour merely reinforcing
 * it — the same reason the answer chip has never been colour alone.
 */

/**
 * The bubble, shared by all three marks so they read as one family.
 *
 * Lifted from lucide's own `message-square-check` / `message-square-x`, which share it exactly. The
 * neutral mark has no lucide equivalent, so it is drawn here on that same path at `M9 11h6` — where
 * the check's own stroke already sits — rather than reaching for a bubble from a different family.
 */
const BUBBLE = 'M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.7.7 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z';

function MessageSquareMinus({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d={BUBBLE} />
      <path d='M9 11h6' />
    </svg>
  );
}

const TONE: Record<FlowCardRef['evaluation'], string> = {
  correct: 'text-ws-success-fg',
  incorrect: 'text-ws-error-fg',
  neutral: 'text-ws-neutral-fg',
};

/** The bubble alone, for a row that supplies its own label — a condition whose feedback is chosen
 * from a dropdown puts the glyph between the two, where "is" would otherwise sit. */
export function FlowCardGlyph({ evaluation, className = 'h-3.5 w-3.5' }: {
  evaluation: FlowCardRef['evaluation'];
  className?: string;
}) {
  const Glyph = evaluation === 'correct' ? MessageSquareCheck
    : evaluation === 'incorrect' ? MessageSquareX
    : MessageSquareMinus;
  return <Glyph aria-hidden='true' className={`shrink-0 ${TONE[evaluation]} ${className}`} />;
}

export function FlowCardToken({ card }: { card: FlowCardRef }) {
  return (
    <span className='inline-flex min-w-0 items-center gap-1 text-xs font-medium text-ws-text'>
      <FlowCardGlyph evaluation={card.evaluation} />
      <span className='min-w-0 truncate'>{card.label}</span>
    </span>
  );
}

/**
 * "Any Block is X or Y" — the cards a rung catches, read as one sentence.
 *
 * The connector is spelled out rather than punctuated: the list is read aloud as often as it is
 * looked at, and a comma between two tokens does not say whether both have to hold.
 */
export function FlowCardTokenList({ lead, cards, connector }: { lead: string; cards: FlowCardRef[]; connector: string }) {
  if (cards.length === 0) return null;
  return (
    <p className='flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ws-muted'>
      <span>{lead}</span>
      {cards.map((card, i) => (
        <span key={card.key} className='inline-flex min-w-0 items-center gap-1.5'>
          {i > 0 && <span>{connector}</span>}
          <FlowCardToken card={card} />
        </span>
      ))}
    </p>
  );
}
